import type { ReactNode } from "react";
import { InstrumentBar, PlayButton, Telemetry, Tick, type Readout } from "./shell";
import { useReducedMotion, useStepper, useSweep, type NonEmpty } from "./useInstrument";

/* =====================================================================
   Pipeline — one sample chart through four stages.

   The figure is a SORTED UNIT PROFILE, not a bar chart: 18 findings packed
   with hairline gaps, worst first, so the shape itself reads as the
   distribution. Severity is ordinal, so it gets a monochrome lightness
   ramp; the accent is reserved for "a pharmacist acted", which is why the
   decision caps at stage 03 are the only colour in the field.

   Every count here is either read off the rule packs (92 in the catalog,
   40 in the long-term-care pack) or is the sample review's own arithmetic
   (3+6+9 = 18 findings, 11+4+3 = 18 decisions). Nothing is rounded up for
   effect — see the header on the section for why that matters.
   ===================================================================== */

type Severity = "crit" | "pend" | "ok";
type Decision = "acc" | "mod" | "dis";

// Sorted worst-first so the profile is a clean descending staircase.
const SEVERITY: readonly Severity[] = [
  "crit", "crit", "crit",
  "pend", "pend", "pend", "pend", "pend", "pend",
  "ok", "ok", "ok", "ok", "ok", "ok", "ok", "ok", "ok",
];

// Dismissals sit at the routine end. You do not dismiss a critical, and a
// graphic that showed otherwise would be saying something untrue.
const DECISION: readonly Decision[] = [
  "acc", "acc", "mod",
  "acc", "mod", "acc", "acc", "mod", "acc",
  "acc", "acc", "dis", "acc", "mod", "acc", "dis", "dis", "acc",
];

type GlyphMode = "raw" | "sev" | "dec" | "seal";

interface Stage {
  n: string;
  segTitle: string;
  segSub: string;
  title: string;
  copy: string;
  tele: readonly Readout[];
  hot: number;
  glyph: GlyphMode;
  legend: ReactNode;
  citedRule: string | null;
  handKey: string;
  handValue: string;
  panel: ReactNode;
}

function MapRow({ from, to }: { from: string; to: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_18px_minmax(0,1.15fr)] items-center gap-2.5 border-b border-ink-100 py-2 last:border-b-0">
      <span className="text-[0.78rem] font-semibold tracking-[0.03em] text-ink-600">{from}</span>
      <svg width="16" height="10" viewBox="0 0 20 10" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" className="text-ink-400">
        <path d="M0 5h17M13 1l4 4-4 4" />
      </svg>
      <span className="text-[0.82rem] text-ink-900">{to}</span>
    </div>
  );
}

function PanelFrame({ title, tag, children, footL, footR }: {
  title: string; tag: string; children: ReactNode; footL: string; footR: string;
}) {
  return (
    <div className="overflow-hidden rounded border border-ink-300 bg-white shadow-card">
      <div className="flex items-center justify-between gap-4 border-b border-border bg-ink-50 px-3.5 py-2.5">
        <span className="text-[0.7rem] font-bold uppercase tracking-[0.12em] text-ink-600">{title}</span>
        <span className="rounded-sm border border-border bg-white px-2 py-0.5 text-[0.7rem] text-ink-600">{tag}</span>
      </div>
      <div className="px-3.5 py-3">{children}</div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-ink-50 px-3.5 py-2.5 text-[0.72rem] text-ink-600">
        <span>{footL}</span>
        <span className="rounded-sm bg-forest-100 px-1.5 py-0.5 text-[0.66rem] font-bold uppercase tracking-[0.07em] text-forest-900">{footR}</span>
      </div>
    </div>
  );
}

const INGEST_PATHWAYS: readonly [string, string, boolean][] = [
  ["Direct EHR connection", "PointClickCare, through their certified data connector", false],
  ["EHR chart export", "The clinical export your EHR already generates", true],
  ["Pharmacy system extract", "The batch files LTC dispensing software produces", true],
  ["MAR and lab PDF", "Read and structured, never retyped", true],
  ["CSV", "For anything that fits none of the above", true],
];

const RULES_FIRED: readonly [string, string, boolean][] = [
  ["Beers 2023 · Table 8", "3 fired", true],
  ["Boustani ACB scale", "2 fired", true],
  ["STOPP/START v2", "6 fired", true],
  ["F605 · §483.45(e)", "7 fired", true],
  ["22 further rules", "evaluated, no finding", false],
];

const STAGES: NonEmpty<Stage> = [
  {
    n: "01",
    segTitle: "Get the chart in",
    segSub: "Five pathways in, one snapshot out",
    title: "Get the chart in, whatever shape it is in",
    copy:
      "Most consultant software waits on an interface project. We read what your systems already produce — a direct EHR connection where one exists, and the export, extract, or PDF where one does not. Nothing is retyped by hand.",
    tele: [
      { value: "5", label: "Ingestion pathways" },
      { value: "None", label: "Interface required" },
      { value: "One", label: "Snapshot format" },
    ],
    hot: 0,
    glyph: "raw",
    legend: (
      <>
        <span className="inst-sw" style={{ color: "var(--i-raw)" }}>18 findings staged</span>
        <em>not yet evaluated</em>
      </>
    ),
    citedRule: null,
    handKey: "Hands to stage 02",
    handValue: "One normalized snapshot — the rules never know which pathway it arrived on",
    panel: (
      <PanelFrame title="Ingestion pathways" tag="One snapshot format" footL="No interface project to start" footR="Same snapshot downstream">
        {INGEST_PATHWAYS.map(([name, sub, live]) => (
          <div key={name} className="grid grid-cols-[14px_minmax(0,1fr)_auto] items-center gap-2.5 border-b border-ink-100 py-2 last:border-b-0">
            <Tick on={live} />
            <span className="text-[0.84rem] font-semibold leading-snug text-ink-900">
              {name}
              <small className="mt-0.5 block text-[0.76rem] font-normal leading-snug text-ink-500">{sub}</small>
            </span>
            <span className={`whitespace-nowrap text-[0.75rem] font-bold ${live ? "text-forest-900" : "text-[#b45309]"}`}>
              {live ? "Live" : "In progress"}
            </span>
          </div>
        ))}
      </PanelFrame>
    ),
  },
  {
    n: "02",
    segTitle: "The clinical engine runs",
    segSub: "Only the packs assigned to that facility",
    title: "The clinical engine runs",
    copy:
      "The rules assigned to that facility evaluate the normalized chart — 40 of them here, out of 92 across the catalog. Every finding that fires carries the published rule it came from.",
    tele: [
      { value: "92", label: "Rules in catalog" },
      { value: "40", label: "Evaluated here" },
      { value: "18", label: "Findings" },
      { value: "0", label: "Uncited" },
    ],
    hot: 3,
    glyph: "sev",
    legend: (
      <>
        <span className="inst-sw" style={{ color: "var(--i-r3)" }}>3 critical</span>
        <span className="inst-sw" style={{ color: "var(--i-r2)" }}>6 moderate</span>
        <span className="inst-sw" style={{ color: "var(--i-r1)" }}>9 routine</span>
        <em>sorted worst first</em>
      </>
    ),
    citedRule: "18 / 18 cited",
    handKey: "Hands to stage 03",
    handValue: "18 findings from 40 rules — each with its published rule already attached",
    panel: (
      <PanelFrame title="Rules evaluated" tag="Deterministic" footL="40 rules evaluated · 18 fired · 18 cited" footR="0 uncited">
        {RULES_FIRED.map(([name, count, fired]) => (
          <div key={name} className="grid grid-cols-[14px_minmax(0,1fr)_auto] items-center gap-2.5 border-b border-ink-100 py-2 last:border-b-0">
            <Tick on={fired} />
            <span className="text-[0.84rem] font-semibold leading-snug text-ink-900">{name}</span>
            <span className="whitespace-nowrap text-[0.75rem] font-bold tabular-nums text-ink-600">{count}</span>
          </div>
        ))}
      </PanelFrame>
    ),
  },
  {
    n: "03",
    segTitle: "The pharmacist decides",
    segSub: "Accept, modify, or dismiss with a reason",
    title: "The pharmacist decides",
    copy:
      "Accept, modify, or dismiss with a reason. Drafts follow your house style; the clinical facts underneath are extracted deterministically, so the model never sources the evidence.",
    tele: [
      { value: "18", label: "Findings" },
      { value: "11", label: "Accepted" },
      { value: "4", label: "Modified" },
      { value: "3", label: "Dismissed" },
    ],
    hot: 0,
    glyph: "dec",
    legend: (
      <>
        <span><i className="inst-key" />11 accepted</span>
        <span><i className="inst-key inst-key-mod" />4 modified</span>
        <span><i className="inst-key inst-key-dis" />3 dismissed, with reason</span>
        <em>dismissals sit at the routine end</em>
      </>
    ),
    citedRule: "18 / 18 reviewed",
    handKey: "Hands to stage 04",
    handValue: "18 decisions — and every dismissal carries its reason",
    panel: (
      <PanelFrame title="Pharmacist decisions" tag="PHI redacted" footL="Every dismissal carries a reason" footR="18 of 18 reviewed">
        {[
          ["Anticholinergic burden ≥ 3 with fall risk", "Taper recommended · sent to prescriber", "Beers 2023 · Table 8", "Accept", "crit"],
          ["Gradual dose reduction due — psychotropic", "Interval changed to 90 days · rationale recorded", "F605 · §483.45(e)(3)", "Modify", "pend"],
          ["Duplicate therapy — proton pump inhibitor", "Dismissed · second agent discontinued at admission", "", "Dismiss", "none"],
        ].map(([title, meta, cite, verdict, sev]) => (
          <div key={title} className="grid grid-cols-[3px_minmax(0,1fr)_auto] items-start gap-2.5 border-b border-ink-100 py-2.5 last:border-b-0">
            <span
              className="self-stretch rounded-sm"
              style={{
                background:
                  sev === "crit" ? "var(--i-r3)" : sev === "pend" ? "var(--i-r2)" : "var(--i-r1)",
              }}
            />
            <span>
              <span className="block text-[0.86rem] font-semibold leading-snug text-ink-900">{title}</span>
              <span className="mt-1 block text-[0.78rem] leading-snug text-ink-500">{meta}</span>
              {cite ? <span className="mt-1.5 inline-block rounded-sm border border-forest-200 bg-forest-50 px-1.5 py-0.5 text-[0.72rem] font-semibold text-forest-900">{cite}</span> : null}
            </span>
            <span className="whitespace-nowrap rounded-sm bg-ink-100 px-1.5 py-0.5 text-[0.66rem] font-bold uppercase tracking-[0.07em] text-ink-600">{verdict}</span>
          </div>
        ))}
      </PanelFrame>
    ),
  },
  {
    n: "04",
    segTitle: "Bundled and billed",
    segSub: "Signature, audit entry, invoice — one pass",
    title: "Bundled and billed",
    copy:
      "In-app signature, append-only audit entry, and the invoice — all from the same review. The bundle the surveyor eventually asks for is already assembled and searchable.",
    tele: [
      { value: "18", label: "Findings sealed" },
      { value: "One", label: "Bundle out" },
      { value: "7 yr", label: "Retention" },
    ],
    hot: 2,
    glyph: "seal",
    legend: (
      <>
        <span className="inst-sw" style={{ color: "var(--i-seal)" }}>18 findings sealed into one bundle</span>
        <em>append-only</em>
      </>
    ),
    citedRule: "sealed · 7-year retention",
    handKey: "Output",
    handValue: "One signed bundle, one invoice, one searchable survey packet",
    panel: (
      <PanelFrame title="Bundle · signed" tag="PHI redacted" footL="Nothing re-keyed between stages" footR="Sealed">
        <MapRow from="Signed by" to="—— · 10:12" />
        <MapRow from="Audit entry" to="Appended · immutable · 7-year retention" />
        <MapRow from="Invoice" to="Drawn from this review · no re-entry" />
        <MapRow from="Survey packet" to="Assembled and searchable on request" />
      </PanelFrame>
    ),
  },
];

function glyphClass(mode: GlyphMode, i: number): string {
  if (mode === "seal") return "inst-g is-sealed";
  if (mode === "raw") return "inst-g";
  const base = `inst-g h-${SEVERITY[i]} c-${SEVERITY[i]}`;
  return mode === "dec" ? `${base} k-${DECISION[i]}` : base;
}

export function PipelineInstrument({ dark = false }: { dark?: boolean }) {
  const reduced = useReducedMotion();
  const { at, current: stage, playing, containerRef, go, toggle, onKeyDown } = useStepper({
    items: STAGES,
    intervalMs: 2100,
    reduced,
  });
  const sweepRef = useSweep(at, reduced);

  return (
    <div ref={containerRef} className={`inst${dark ? " is-dark" : ""}`}>
      <div className="inst-device">
        <InstrumentBar title="Pipeline · one sample chart, four stages">
          <span className="inst-pos">Stage {stage.n} / 04</span>
          <PlayButton
            playing={playing}
            atEnd={at === STAGES.length - 1}
            onClick={toggle}
            label="Play the pipeline from this stage"
          />
        </InstrumentBar>

        <div className="inst-plot">
          <span ref={sweepRef} className="inst-sweep" aria-hidden="true" />

          <Telemetry readouts={stage.tele} hot={stage.hot} reduced={reduced} />

          <div
            className={`inst-field${stage.glyph === "seal" ? " is-merged" : ""}`}
            role="img"
            aria-label="Eighteen findings from one chart, changing state as the review moves through the pipeline."
          >
            {SEVERITY.map((_, i) => (
              <span key={i} className={glyphClass(stage.glyph, i)} />
            ))}
          </div>
          {/* One mark, two jobs. It is the axis the profile stands on, and
              when every finding carries a citation it turns accent and names
              the count — rather than stacking a second rule underneath. */}
          <div className={`inst-axis${stage.citedRule ? " is-cited" : ""}`} aria-hidden="true">
            <span className="inst-axis-line" />
            <em>{stage.citedRule ?? ""}</em>
          </div>
          <div className="inst-legend">{stage.legend}</div>
        </div>

        <div className="inst-ruler" role="tablist" aria-label="Pipeline stages" onKeyDown={onKeyDown}>
          <span
            className="inst-ruler-fill"
            style={{ width: `${((at + 1) / STAGES.length) * 100}%` }}
            aria-hidden="true"
          />
          {STAGES.map((s, i) => (
            <button
              key={s.n}
              type="button"
              role="tab"
              aria-selected={i === at}
              tabIndex={i === at ? 0 : -1}
              id={`pipeline-tab-${s.n}`}
              aria-controls={`pipeline-panel-${s.n}`}
              className={`inst-seg${i === at ? " is-on" : ""}${i < at ? " is-done" : ""}`}
              onClick={() => go(i)}
            >
              <span className="inst-seg-n">{s.n} — {s.segTitle === "Get the chart in" ? "Ingest" : s.segTitle === "The clinical engine runs" ? "Flag" : s.segTitle === "The pharmacist decides" ? "Review" : "Sign & invoice"}</span>
              <span className="inst-seg-t">{s.segTitle}</span>
              <span className="inst-seg-s">{s.segSub}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Every stage panel is rendered and the inactive ones are hidden,
          rather than only mounting the current one. Two reasons: it is the
          ARIA tabs pattern, and it keeps all four stages' copy in the
          prerendered HTML — mounting one at a time silently dropped three
          quarters of this section from the static page. */}
      {STAGES.map((s, i) => (
        <div
          key={s.n}
          id={`pipeline-panel-${s.n}`}
          role="tabpanel"
          aria-labelledby={`pipeline-tab-${s.n}`}
          hidden={i !== at}
        >
          <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)] lg:gap-10">
            <div className={`flex flex-col${i === at ? " inst-fade" : ""}`} key={`copy-${at}`}>
              <h3 className="text-[1.35rem] font-bold tracking-tightest text-ink-900">{s.title}</h3>
              <p className="mt-3 max-w-[34em] text-[0.95rem] leading-relaxed text-ink-600">{s.copy}</p>
              <p className="mt-auto pt-5">
                <span className="block border-t-[1.5px] border-ink-900 pt-2.5 text-[0.63rem] font-bold uppercase tracking-[0.14em] text-forest-900">
                  {s.handKey}
                </span>
                <span className="mt-1.5 block text-[0.9rem] font-semibold leading-snug tracking-[-0.014em] text-ink-900">
                  {s.handValue}
                </span>
              </p>
            </div>
            <div className={i === at ? "inst-fade" : undefined} key={`panel-${at}`}>{s.panel}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
